import type { Document, DocumentType, Job } from '../../core/types/data-models';
import type { WizardStep } from '../../shared/components/WizardStepper';

/** Document statuses that mean "generated and visible" — a row still IN_GENERATION (or failed) is not a stage result. */
const VISIBLE_DOCUMENT_STATUSES = new Set<string>(['DRAFT', 'IN_REVIEW', 'APPROVED']);

export interface ValidationStage {
  /** 1-based position in the category order. */
  index: number;
  docType: DocumentType;
  /** The project's generated document of this type, or null while not generated. */
  document: Document | null;
}

/**
 * The validation category's active root document types in catalog order,
 * each paired with the project's generated document of that type. One
 * stage = one document; the "next step" is the first stage without one.
 * The category comes from the project type's profile (IDEA_VALIDATION or
 * BUSINESS_VALIDATION); a type without a guided validation has no stages.
 */
export function buildValidationStages(
  docTypes: DocumentType[],
  documents: Document[],
  category: string | null,
): ValidationStage[] {
  if (!category) return [];
  const latestByType = new Map<string, Document>();
  for (const doc of documents) {
    if (!VISIBLE_DOCUMENT_STATUSES.has(doc.status)) continue;
    const current = latestByType.get(doc.documentType);
    if (!current || doc.id > current.id) latestByType.set(doc.documentType, doc);
  }
  return docTypes
    .filter((dt) => dt.categoryType === category && dt.status === 'Active' && dt.parentDocumentType === null)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    .map((docType, i) => ({ index: i + 1, docType, document: latestByType.get(docType.type) ?? null }));
}

/** The document type a GENERATE_DOCUMENT job is producing, read from its data. */
export function jobDocumentType(job: Job): string | null {
  if (!job.data) return null;
  try {
    return (JSON.parse(job.data) as { documentType?: string }).documentType ?? null;
  } catch {
    return null;
  }
}

export const INTRO_STEP_KEY = 'intro';
export const SUMMARY_STEP_KEY = 'summary';

/**
 * The first step's journey state: done (the idea is written, or the company
 * brief exists), running (the sources are being analyzed) or active (still
 * to do — in the wizard, or a business project whose sources were never
 * analyzed).
 */
export type IntroStepState = 'done' | 'active' | 'running';

/**
 * The steps of the whole journey, for the shared stepper: the first step
 * (describe the idea, or the company sources), one step per validation
 * stage (keyed by its document type), and "Summary". Used by the onboarding
 * wizards (intro active, nothing generated yet) and by the validation page.
 */
export function buildValidationSteps(
  stages: ValidationStage[],
  options: {
    intro: { label: string; state: IntroStepState; clickable: boolean };
    generatingType: string | null;
    /** The summary row's state: ready to read, or being generated. Absent in the wizard, where nothing exists yet. */
    summary?: { ready: boolean; running: boolean };
  },
): WizardStep[] {
  const generated = stages.filter((s) => s.document).length;
  const allGenerated = stages.length > 0 && generated === stages.length;
  const summary = options.summary ?? { ready: false, running: false };
  const introDone = options.intro.state === 'done';
  // The journey's current stage: the one running, else the first without a
  // document — once the first step is done; before that the first step is current.
  const currentType =
    introDone && !options.generatingType ? (stages.find((s) => !s.document)?.docType.type ?? null) : null;
  // Once every stage exists, the summary is the journey's current step until it is built.
  const summaryState: WizardStep['state'] = summary.running
    ? 'running'
    : summary.ready
      ? 'done'
      : allGenerated && introDone && !options.generatingType
        ? 'active'
        : 'todo';
  return [
    {
      key: INTRO_STEP_KEY,
      label: options.intro.label,
      state: options.intro.state,
      clickable: options.intro.clickable,
    },
    ...stages.map<WizardStep>((stage) => {
      const running = options.generatingType === stage.docType.type;
      return {
        key: stage.docType.type,
        label: stage.docType.name,
        state: running ? 'running' : stage.document ? 'done' : stage.docType.type === currentType ? 'active' : 'todo',
        clickable: running || stage.document != null,
      };
    }),
    {
      key: SUMMARY_STEP_KEY,
      label: 'Summary',
      state: summaryState,
      clickable: generated > 0,
    },
  ];
}
